/**
 * Tooltip formatter 入口模块。
 * 负责组织排序、列/记录生成和 HTML 渲染的整体流程，不在此文件编写底层实现细节。
 * 若需新增 formatter 变体或组合策略，请在此文件扩展。
 */
import { generateTooltipGrid } from './render';
import { createTooltipSortFunction } from './sorting';
import { getTooltipColumnAndRecordsFunction } from './transformers';
import { IColumn, ITooltipParam, ITooltipRecord } from '@customComp/Chart/chartConfigs/types';
import { IUserGroup } from "@/pages/datalab/components/UserFilter/FilterPanel/type";

interface ITooltipFormatterConfig {
  metricNames?: string[];
  compareDateCount?: number;
  isOnlyOneDimension: boolean;
  userFilterControls?: IUserGroup[];
  stackedDynamicState?: boolean;
}

interface ITooltipFormatterDeps {
  toolTipSort: (params: ITooltipParam[]) => ITooltipParam[];
  generateColumnAndRecords: (params: ITooltipParam[]) => {
    columns: IColumn[];
    records: ITooltipRecord[];
  };
}

const normalizeTooltipParams = (params: ITooltipParam | ITooltipParam[]): ITooltipParam[] => (
  Array.isArray(params) ? params : [params]
);

export const createTooltipFormat = ({
  toolTipSort,
  generateColumnAndRecords,
}: ITooltipFormatterDeps): ((params: ITooltipParam | ITooltipParam[]) => string) => {
  return (params: ITooltipParam | ITooltipParam[]): string => {
    try {
      let tooltipParams = Array.isArray(params) ? params : [params];
      // 先删除 data不是对象的数据, 比如参考线的数据, 不需要展示在浮窗中.
      tooltipParams = tooltipParams?.filter(item => (typeof item.data === 'object'));
      // 先排序
      tooltipParams = toolTipSort(tooltipParams);
      // 再转换为 columns和records结构
      const { columns, records } = generateColumnAndRecords(tooltipParams);
      // 最后 生成html字符串
      const result = generateTooltipGrid(columns, records);
      // 确保返回字符串，避免返回 null 或 undefined
      return result || '';
    } catch (error) {
      console.warn('[createTooltipFormat] Tooltip formatter error:', error);
      return ''; // 返回空字符串而不是 null/undefined
    }
  };
};

export const getDatalabTooltipFormatter = (
  config: ITooltipFormatterConfig,
): ((params: ITooltipParam[]) => string) => {
  const { metricNames, compareDateCount, isOnlyOneDimension, userFilterControls, stackedDynamicState } = config;
  const toolTipSort = createTooltipSortFunction({
    metricNames,
    compareDateCount,
    isOnlyOneDimension,
    stackedDynamicState,
  });
  const generateColumnAndRecords = getTooltipColumnAndRecordsFunction({
    compareDateCount,
    isOnlyOneDimension,
  });

  const formatter = createTooltipFormat({ toolTipSort, generateColumnAndRecords });

  const shouldSplitByUserFilter = (userFilterControls?.length || 0) > 1;
  if (!shouldSplitByUserFilter) {
    return formatter;
  }

  return (params: ITooltipParam[] | ITooltipParam): string => {
    try {
      const result = buildUserFilterTooltip(normalizeTooltipParams(params), formatter);
      return result || '';
    } catch (error) {
      console.warn('[getDatalabTooltipFormatter] Tooltip formatter error:', error);
      return '';
    }
  };
};

// @ts-ignore
const buildUserFilterTooltip = (
  tooltipParams: ITooltipParam[],
  formatter: (params: ITooltipParam[]) => string,
): string => {
  try {
    const controlMap: Record<string, ITooltipParam[]> = {};
    tooltipParams.forEach((param) => {
      if (typeof param.data !== 'object') return; // 增加了参考线的数据, data不是对象, 不需要显示在浮窗中.
      const controlName = param?.data?.controlName || '';
      const arr = controlMap[controlName] ?? [];
      arr.push(param);
      controlMap[controlName] = arr;
    });

    const controlNames = Object.keys(controlMap);
    const result = controlNames
      .map((name, index) => {
        const body = formatter(controlMap[name]) || '';
        const title = `<div class="dbl-chart-tooltip-user-filter__title">${name} </div> ${body}`;
        const vsDivider = index < controlNames.length - 1 ? '<div class="dbl-chart-tooltip-user-filter__vs">VS</div>' : '';
        return `${title}${vsDivider}`;
      })
      .join('');
    return result || '';
  } catch (error) {
    console.warn('[buildUserFilterTooltip] Error building user filter tooltip:', error);
    return '';
  }
};
